import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/lib/cart";
import { inr } from "@/lib/content";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal, count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" data-testid="cart-drawer">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            data-testid="cart-drawer-overlay"
            className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-surface"
            data-testid="cart-drawer-panel"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h2 className="font-display text-2xl tracking-wide text-bone">
                YOUR BAG{" "}
                <span className="align-middle font-mono text-xs text-bone/50">({count})</span>
              </h2>
              <button
                data-testid="cart-drawer-close-button"
                aria-label="Close bag"
                onClick={() => setOpen(false)}
                className="p-2 text-bone/60 transition-colors hover:text-gold"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <ShoppingBag className="h-8 w-8 text-bone/30" strokeWidth={1.25} />
                <p className="font-display text-3xl tracking-wide text-bone">YOUR BAG IS EMPTY</p>
                <p className="max-w-[220px] font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                  Your pieces are waiting.
                </p>
                <button
                  data-testid="cart-empty-shop-button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/shop");
                  }}
                  className="mt-2 border border-gold px-8 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  SHOP THE DROP
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-2">
                  {items.map((it) => {
                    const suffix = it.size || it.variant;
                    return (
                      <div
                        key={`${it.slug}-${suffix}`}
                        data-testid={`cart-item-${it.slug}-${suffix}`}
                        className="flex gap-4 border-b border-white/5 py-4"
                      >
                        <img src={it.image || "/logo.svg"} alt={it.name} className="h-24 w-20 shrink-0 object-cover" />
                        <div className="flex flex-1 flex-col justify-between py-0.5">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-display text-lg leading-tight tracking-wide text-bone">
                                {it.name}
                              </p>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50">
                                {[it.colour, it.variant, it.size && `SIZE ${it.size}`].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                            <p className="font-mono text-xs text-bone">{inr(it.price * it.qty)}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center border border-line">
                              <button
                                data-testid={`cart-item-minus-${it.slug}-${suffix}`}
                                aria-label="Decrease quantity"
                                onClick={() =>
                                  it.qty === 1
                                    ? remove(it.slug, it.size, it.variant)
                                    : updateQty(it.slug, it.size, it.variant, it.qty - 1)
                                }
                                className="p-2.5 text-bone/60 transition-colors hover:text-gold"
                              >
                                <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                              <span
                                data-testid={`cart-item-qty-${it.slug}-${suffix}`}
                                className="w-8 text-center font-mono text-xs text-bone"
                              >
                                {it.qty}
                              </span>
                              <button
                                data-testid={`cart-item-plus-${it.slug}-${suffix}`}
                                aria-label="Increase quantity"
                                onClick={() => updateQty(it.slug, it.size, it.variant, it.qty + 1)}
                                className="p-2.5 text-bone/60 transition-colors hover:text-gold"
                              >
                                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                            </div>
                            <button
                              data-testid={`cart-item-remove-${it.slug}-${suffix}`}
                              onClick={() => remove(it.slug, it.size, it.variant)}
                              className="-my-2 p-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/40 transition-colors hover:text-magenta"
                            >
                              REMOVE
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-white/10 px-6 pb-[max(20px,env(safe-area-inset-bottom))] pt-5">
                  <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-bone/60">
                    <span>SUBTOTAL</span>
                    <span data-testid="cart-subtotal" className="text-sm text-bone">
                      {inr(subtotal)}
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    FREE SHIPPING ACROSS INDIA
                  </p>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={checkout}
                    className="mt-4 w-full bg-gold py-4 font-display text-xl tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-bone"
                  >
                    PROCEED TO CHECKOUT
                  </button>
                  <button
                    data-testid="cart-continue-button"
                    onClick={() => setOpen(false)}
                    className="mt-3 w-full py-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 transition-colors hover:text-bone"
                  >
                    OR KEEP SHOPPING
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
